const enhancedDialogs = new WeakSet<HTMLDialogElement>();
const enhancedTablists = new WeakSet<HTMLElement>();
const enhancedGroups = new WeakSet<HTMLElement>();

let activeDialogId: string | null = null;
let returnFocusSelector: string | null = null;
let enhancementQueued = false;

const dialogOpenActions = new Set([
  "open-queue",
  "open-layout",
  "open-timeline",
  "open-timeline-filter",
  "open-record",
]);

function escapeAttribute(value: string): string {
  return value.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
}

function stableSelector(element: HTMLElement | null): string | null {
  if (!element) return null;
  if (element.id) return `#${element.id}`;

  const action = element.dataset.action;
  if (!action) return null;
  const parts = [`[data-action="${escapeAttribute(action)}"]`];
  for (const key of ["flowStage", "eventType", "encounterId", "tab", "modality"]) {
    const value = element.dataset[key];
    if (!value) continue;
    const attribute = key.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`);
    parts.push(`[data-${attribute}="${escapeAttribute(value)}"]`);
  }
  return parts.join("");
}

function rememberDialogOpener(event: Event): void {
  const target = event.target instanceof Element
    ? event.target.closest<HTMLElement>("[data-action]")
    : null;
  if (!target || !dialogOpenActions.has(target.dataset.action ?? "")) return;
  returnFocusSelector = stableSelector(target);
}

function focusFirstControl(dialog: HTMLDialogElement): void {
  const preferred = dialog.querySelector<HTMLElement>(
    "#timeline-search, [autofocus], .dialog-close, input:not([disabled]), select:not([disabled]), button:not([disabled]), a[href], [tabindex]:not([tabindex='-1'])",
  );
  preferred?.focus({ preventScroll: true });
}

function enhanceDialog(dialog: HTMLDialogElement): void {
  dialog.setAttribute("aria-modal", "true");
  if (enhancedDialogs.has(dialog)) return;
  enhancedDialogs.add(dialog);

  dialog.addEventListener("cancel", (event) => {
    event.preventDefault();
    const closeButton = dialog.querySelector<HTMLButtonElement>(
      ".dialog-close, [data-action^='close-']",
    );
    closeButton?.click();
  });
}

function syncDialogFocus(): void {
  const openDialog = document.querySelector<HTMLDialogElement>("dialog[open]");
  if (openDialog) {
    if (activeDialogId !== openDialog.id) {
      activeDialogId = openDialog.id;
      requestAnimationFrame(() => focusFirstControl(openDialog));
    }
    return;
  }

  if (!activeDialogId) return;
  activeDialogId = null;
  const selector = returnFocusSelector;
  returnFocusSelector = null;
  requestAnimationFrame(() => {
    if (!selector) return;
    document.querySelector<HTMLElement>(selector)?.focus({ preventScroll: true });
  });
}

function enhanceTablist(container: HTMLElement): void {
  container.setAttribute("role", "tablist");
  const buttons = Array.from(container.querySelectorAll<HTMLButtonElement>("button"));
  buttons.forEach((button) => {
    const selected = button.classList.contains("active");
    button.setAttribute("role", "tab");
    button.setAttribute("aria-selected", String(selected));
    button.tabIndex = selected ? 0 : -1;
  });

  if (enhancedTablists.has(container)) return;
  enhancedTablists.add(container);
  container.addEventListener("keydown", (event) => {
    if (!(event instanceof KeyboardEvent)) return;
    if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
    const currentButtons = Array.from(
      container.querySelectorAll<HTMLButtonElement>("button:not([disabled])"),
    );
    if (!currentButtons.length) return;
    const activeIndex = Math.max(0, currentButtons.indexOf(document.activeElement as HTMLButtonElement));
    const nextIndex =
      event.key === "Home"
        ? 0
        : event.key === "End"
          ? currentButtons.length - 1
          : event.key === "ArrowRight"
            ? (activeIndex + 1) % currentButtons.length
            : (activeIndex - 1 + currentButtons.length) % currentButtons.length;
    event.preventDefault();
    currentButtons[nextIndex]?.focus();
    currentButtons[nextIndex]?.click();
  });
}

function enhanceChoiceGroup(container: HTMLElement, fallbackLabel: string): void {
  container.setAttribute("role", "group");
  if (!container.getAttribute("aria-label")) container.setAttribute("aria-label", fallbackLabel);
  container.querySelectorAll<HTMLButtonElement>("button").forEach((button) => {
    button.setAttribute("aria-pressed", String(button.classList.contains("active")));
  });
  enhancedGroups.add(container);
}

function ensureLabel(selector: string, label: string): void {
  const element = document.querySelector<HTMLElement>(selector);
  if (element && !element.getAttribute("aria-label") && !element.getAttribute("aria-labelledby")) {
    element.setAttribute("aria-label", label);
  }
}

function ensureSkipLink(): void {
  const workspace = document.querySelector<HTMLElement>("main.workspace");
  if (!workspace) return;
  workspace.id = "clinical-workspace";
  workspace.tabIndex = -1;

  if (document.querySelector(".skip-link")) return;
  const link = document.createElement("a");
  link.className = "skip-link";
  link.href = "#clinical-workspace";
  link.textContent = "진료 차트로 바로가기";
  document.body.prepend(link);
}

function enhanceAccessibility(): void {
  ensureSkipLink();

  document.querySelectorAll<HTMLDialogElement>("dialog").forEach(enhanceDialog);
  document
    .querySelectorAll<HTMLElement>(".context-tabs, .mobile-clinical-nav")
    .forEach(enhanceTablist);

  const groups: Array<[string, string]> = [
    [".order-modality-tabs", "오더 종류 선택"],
    [".timeline-filters", "과거 기록 유형 필터"],
    [".queue-dialog-filters", "환자 흐름 필터"],
    [".layout-presets", "화면 구성 선택"],
    [".body-region-chips", "신체 부위 선택"],
  ];
  groups.forEach(([selector, label]) => {
    document
      .querySelectorAll<HTMLElement>(selector)
      .forEach((container) => enhanceChoiceGroup(container, label));
  });

  ensureLabel("#order-search", "검사·치료 오더 검색");
  ensureLabel("#assistant-query", "존에게 현재 환자에 대해 질문");
  ensureLabel("#chart-percent", "진료 차트 면적 비율");

  document.querySelector<HTMLElement>("#note-save-state")?.setAttribute("role", "status");
  document.querySelector<HTMLElement>("#note-save-state")?.setAttribute("aria-live", "polite");
  document.querySelector<HTMLElement>(".voice-status")?.setAttribute("role", "status");

  syncDialogFocus();
}

function queueEnhancement(): void {
  if (enhancementQueued) return;
  enhancementQueued = true;
  queueMicrotask(() => {
    enhancementQueued = false;
    enhanceAccessibility();
  });
}

document.addEventListener("click", rememberDialogOpener, true);
document.addEventListener("DOMContentLoaded", queueEnhancement, { once: true });

const observer = new MutationObserver(queueEnhancement);
observer.observe(document.documentElement, {
  childList: true,
  subtree: true,
  attributes: true,
  attributeFilter: ["open", "class", "disabled"],
});

queueEnhancement();
